import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

// Icon component matching landing page style
const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const TermsOfService = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        // Scrolling up or near top - show header
        setIsVisible(true);
      } else {
        // Scrolling down - hide header
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Terms of Service | QuoteTier"
        description="Read QuoteTier's Terms of Service to understand your rights and obligations when using our platform."
        canonical="/terms"
      />
      {/* Standardized floating header matching main site */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup">
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup">
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content with spacing for floating header */}
      <main className="py-16 mt-20">
        <div className="container max-w-4xl mx-auto px-4">
          {/* Title Section */}
          <div className="text-center mb-16">
            <h1 className="font-instrument text-4xl sm:text-5xl font-bold tracking-tight mb-4">Terms of Service</h1>
            <p className="font-jetbrains text-muted-foreground text-lg tracking-tight">Last updated: {currentDate}</p>
          </div>

          {/* Content */}
          <div className="bg-white border-2 border-black rounded-lg p-8 sm:p-12">
            <div className="prose prose-lg max-w-none">
              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Agreement to Terms</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  These Terms of Service ("Terms") govern your use of the QuoteTier web application and services (the "Service") operated by QuoteTier ("us," "we," or "our").
                </p>
                <p className="font-jetbrains text-gray-700 leading-relaxed">
                  By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these terms, then you may not access the Service.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Description of Service</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  QuoteTier is a Software as a Service (SaaS) platform that enables businesses to:
                </p>
                <ul className="font-jetbrains space-y-2 mb-8 text-gray-700">
                  <li>• Create and manage professional quotes with Good/Better/Best pricing tiers</li>
                  <li>• Generate shareable quote links for clients</li>
                  <li>• Track quote views and engagement analytics</li>
                  <li>• Collect digital signatures and quote acceptances</li>
                  <li>• Manage interactive product catalogs and upsells</li>
                  <li>• Generate professional quote documents and confirmations</li>
                </ul>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Subscription and Payment Terms</h2>
                
                <h3 className="font-instrument text-xl font-medium mb-4">Free Trial</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-6">
                  We offer a free forever plan with 3 quotes per month. No payment method required. Pro plans are billed monthly and can be cancelled anytime.
                </p>

                <h3 className="font-instrument text-xl font-medium mb-4">Subscription Billing</h3>
                <ul className="font-jetbrains space-y-2 mb-6 text-gray-700">
                  <li>• Subscriptions are billed monthly or annually in advance</li>
                  <li>• All fees are non-refundable except as required by law</li>
                  <li>• We may change pricing with 30 days advance notice</li>
                  <li>• Failed payments may result in service suspension</li>
                  <li>• You may cancel your subscription at any time through your account settings</li>
                </ul>

                <h3 className="font-instrument text-xl font-medium mb-4">Refund Policy</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-8">
                  We offer a 30-day money-back guarantee for new subscribers. Refund requests must be submitted within 30 days of your initial payment.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Acceptable Use Policy</h2>
                
                <h3 className="font-instrument text-xl font-medium mb-4">Permitted Uses</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-6">
                  You may use our Service only for lawful business purposes in accordance with these Terms.
                </p>

                <h3 className="font-instrument text-xl font-medium mb-4">Prohibited Activities</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">You may not:</p>
                <ul className="font-jetbrains space-y-2 mb-8 text-gray-700">
                  <li>• Use the Service for any illegal or unauthorized purpose</li>
                  <li>• Violate any applicable laws or regulations</li>
                  <li>• Infringe on intellectual property rights of others</li>
                  <li>• Upload malicious code, viruses, or harmful content</li>
                  <li>• Attempt to gain unauthorized access to our systems</li>
                  <li>• Interfere with or disrupt the Service or servers</li>
                  <li>• Use the Service to spam, harass, or solicit others</li>
                  <li>• Reverse engineer, decompile, or disassemble the Service</li>
                </ul>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">User Content and Data</h2>
                
                <h3 className="font-instrument text-xl font-medium mb-4">Your Content</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-6">
                  You retain ownership of all content you submit to the Service, including quotes, client information, business data, and uploaded files. You grant us a limited license to use this content solely to provide the Service.
                </p>

                <h3 className="font-instrument text-xl font-medium mb-4">Content Responsibility</h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">You are solely responsible for:</p>
                <ul className="font-jetbrains space-y-2 mb-8 text-gray-700">
                  <li>• The accuracy and legality of your content</li>
                  <li>• Obtaining necessary permissions for content use</li>
                  <li>• Ensuring content does not violate third-party rights</li>
                  <li>• Backing up your data (we recommend regular exports)</li>
                </ul>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Service Availability</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-6">
                  We strive to maintain 99.9% uptime but do not guarantee uninterrupted access to the Service. We may experience downtime for maintenance, updates, or technical issues.
                </p>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-8">
                  We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Limitation of Liability</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-6">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-8">
                  IN NO EVENT SHALL QUOTETIER BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Termination</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  You may terminate your account at any time by canceling your subscription through your account settings or contacting support.
                </p>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  We may terminate or suspend your account immediately if you violate these Terms, fail to pay fees when due, engage in fraudulent activity, or pose a security risk.
                </p>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-8">
                  Upon termination, your right to use the Service will cease immediately. We will provide you with a reasonable opportunity to export your data before permanent deletion.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-instrument text-2xl font-semibold mb-6">Contact Information</h2>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  If you have any questions about these Terms of Service, please contact us through our platform's support system or reach out to us directly.
                </p>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  You can also review our <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</Link> or return to our <Link to="/" className="text-blue-600 hover:text-blue-800 underline">homepage</Link> to learn more about QuoteTier's interactive quote builder.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Footer matching landing page */}
      <footer className="border-t-2 border-black">
        <div className="container max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row justify-between items-center">
          <p className="font-jetbrains text-sm text-muted-foreground">&copy; 2025 QuoteTier. All rights reserved. <span className="footer-credit">Site by <a href="https://novanorthstudio.com/?utm_source=quotetier.com&utm_medium=referral&utm_campaign=site-credit">Nova North Studio</a></span></p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="/terms" className="font-jetbrains text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="font-jetbrains text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            <span className="font-jetbrains text-sm text-muted-foreground">Contact</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
